import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ViewChild,
  inject,
  output,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { Option } from '@interfaces/common/option';
import {
  ClientContractAutomaticData,
  ClientContractResponse,
  ClientContractType,
} from '@interfaces/companys/contracts/responses/client-contract.response';
import { TranslateService } from '@ngx-translate/core';
import { ToastHelperService } from '@services/helpers/toast-helper.service';
import { ClientContractsHttpService } from '@services/http/companys/client-contracts-http.service';
import { VaultBaseComponent } from '@src/app/shared/components/base/vault-base.component';
import { DialogComponent } from '@src/app/shared/components/dialog/dialog.component';
import { SharedModule } from '@src/app/shared/shared.module';
import { finalize } from 'rxjs';
import { backendErrorMessage } from '../../../../../../partner/utils/partner-contract.utils';
import { ClientContractFieldsForm } from '../../../../utils/client-contract-fields-form';
import {
  CLIENT_CONTRACT_TYPES,
  clientContractTypeLabel,
} from '../../../../utils/client-contract.utils';
import { ClientContractAutomaticDataComponent } from '../../../client-contract-automatic-data/client-contract-automatic-data.component';
import { ClientContractFieldsFormComponent } from '../../../client-contract-fields-form/client-contract-fields-form.component';

/** Empresa para a qual o contrato vai ser gerado. */
export interface GenerateClientContractContext {
  companyId: string;
  companyName: string;
}

/**
 * Gera a minuta de um contrato de cliente (card 3643): tipo (Flat Fee ou Gestão Ativa), campos do
 * catálogo já com os padrões, blocos do GA e, só para leitura, os dados do cadastro da empresa que
 * vão para o texto (com o aviso do que falta). Ao gerar, emite a minuta; quem abriu navega para o
 * editor.
 *
 * Os dados do cadastro e o que falta vêm do backend (`GET client-contracts/automatic`), com as mesmas
 * regras do contrato gerado.
 */
@Component({
  selector: 'app-modal-generate-client-contract',
  standalone: true,
  templateUrl: './modal-generate-client-contract.component.html',
  styleUrls: ['./modal-generate-client-contract.component.scss'],
  imports: [
    CommonModule,
    ReactiveFormsModule,
    SharedModule,
    ClientContractFieldsFormComponent,
    ClientContractAutomaticDataComponent,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ModalGenerateClientContractComponent extends VaultBaseComponent {
  @ViewChild('dialog') dialogRef: DialogComponent | null = null;

  private readonly contractsService = inject(ClientContractsHttpService);
  private readonly toast = inject(ToastHelperService);
  private readonly destroyRef = inject(DestroyRef);

  /** Minuta criada; o pai navega para o editor. */
  readonly generated = output<ClientContractResponse>();

  readonly typeOptions: Option[] = CLIENT_CONTRACT_TYPES.map(type => ({
    label: clientContractTypeLabel(type),
    value: type,
  }));
  readonly typeControl = new FormControl<ClientContractType>('FlatFee', { nonNullable: true });
  readonly titleControl = new FormControl<string>('', { nonNullable: true });
  readonly fieldsForm = new ClientContractFieldsForm();

  readonly companyId = signal<string | null>(null);
  readonly companyName = signal('');
  /** Gravando. Nome próprio porque `isLoading` do `VaultBaseComponent` é o spinner global (NgRx). */
  readonly saving = signal(false);
  readonly catalogLoading = signal(false);
  readonly automaticLoading = signal(false);
  readonly automatic = signal<ClientContractAutomaticData | null>(null);
  readonly missingAutomatic = signal<string[]>([]);

  constructor(translateService: TranslateService) {
    super(translateService);
    this.typeControl.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(type => this.loadCatalog(type));
  }

  /** Abre a modal para a empresa, com Flat Fee e os padrões do catálogo. */
  open(context: GenerateClientContractContext): void {
    this.companyId.set(context.companyId);
    this.companyName.set(context.companyName);
    this.saving.set(false);
    this.titleControl.setValue('');
    this.typeControl.setValue('FlatFee', { emitEvent: false });
    this.loadCatalog('FlatFee');
    this.loadAutomaticData(context.companyId);
    this.dialogRef?.open('', '', '', 'Cancelar', '', 'generate-client-contract', false, true);
  }

  isFormValid(): boolean {
    return (
      !!this.companyId() &&
      !this.catalogLoading() &&
      this.fieldsForm.entries.length > 0 &&
      this.fieldsForm.root.valid
    );
  }

  /** "Corrigir no cadastro": o cadastro da empresa é a tela de trás; fecha a modal. */
  closeToFixRegistration(): void {
    this.dialogRef?.close();
    this.toast.showToastInfo(
      'Corrija o cadastro e gere de novo',
      'Endereço fica no bloco de endereço; o representante legal é o contato marcado como "Representante legal" em Contatos. Salve a empresa antes de gerar.'
    );
  }

  submitAction(): void {
    const companyId = this.companyId();
    if (!companyId || this.saving()) return;
    if (!this.isFormValid()) {
      this.fieldsForm.root.markAllAsTouched();
      const invalid = this.fieldsForm.invalidLabels();
      if (invalid.length) {
        this.toast.showToastWarn(
          'Há valores fora dos limites',
          `Corrija: ${invalid.join(', ')}. Percentuais vão de 0 a 100.`
        );
      }
      return;
    }

    this.saving.set(true);
    this.contractsService
      .createContract({
        companyId,
        type: this.typeControl.value,
        title: this.titleControl.value.trim() || null,
        fields: this.fieldsForm.readFields(),
        blocks: this.fieldsForm.readBlocks(),
      })
      .pipe(
        finalize(() => this.saving.set(false)),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe({
        next: contract => {
          if (!contract) return;
          const pending = contract.missingAutomatic?.length || contract.unfilledVariables?.length;
          this.toast.showToastSuccess(
            `Contrato ${contract.contractNumber} gerado`,
            pending
              ? 'Alguns dados ficaram em branco e aparecem em amarelo no texto. Complete no editor ou no cadastro da empresa.'
              : 'O texto já saiu com os dados da empresa e os valores escolhidos. Revise no editor.'
          );
          this.dialogRef?.close();
          this.generated.emit(contract);
        },
        error: error =>
          this.toast.showToastError(
            'Não foi possível gerar o contrato',
            backendErrorMessage(error) || 'Confira os valores e tente de novo.'
          ),
      });
  }

  private loadCatalog(type: ClientContractType): void {
    this.catalogLoading.set(true);
    this.contractsService
      .getFieldCatalog(type)
      .pipe(
        finalize(() => this.catalogLoading.set(false)),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe({
        // Resposta de um tipo que a pessoa já trocou é descartada: o formulário não pode ficar com o catálogo de um
        // tipo e gerar o contrato do outro.
        next: catalog => {
          if (type === this.typeControl.value) this.fieldsForm.setCatalog(catalog);
        },
        error: error => {
          if (type !== this.typeControl.value) return;
          this.fieldsForm.setCatalog({ fields: [], blocks: [] });
          this.toast.showToastError(
            `Não foi possível carregar os campos do ${clientContractTypeLabel(type)}`,
            backendErrorMessage(error) || 'Feche a modal e tente de novo.'
          );
        },
      });
  }

  /**
   * Prévia dos dados do cadastro e do que falta, pelo backend (`GET client-contracts/automatic`), com as
   * mesmas regras do contrato gerado. Sem resposta, a modal segue sem a prévia (gerar continua possível).
   */
  private loadAutomaticData(companyId: string): void {
    this.automaticLoading.set(true);
    this.automatic.set(null);
    this.missingAutomatic.set([]);
    this.contractsService
      .getAutomaticPreview(companyId)
      .pipe(
        finalize(() => this.automaticLoading.set(false)),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe({
        next: preview => {
          this.automatic.set(preview?.automatic ?? null);
          this.missingAutomatic.set(preview?.missingAutomatic ?? []);
        },
        error: error =>
          this.toast.showToastWarn(
            'Não foi possível conferir o cadastro da empresa',
            backendErrorMessage(error) ||
              'Dá para gerar assim mesmo; o editor mostra o que faltar no cadastro.'
          ),
      });
  }
}
